import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Settings, Users, Send, Bot, Loader2, ExternalLink, CheckCircle2, XCircle, Eye, EyeOff, Bell, RefreshCw, Building2 } from "lucide-react";

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [config, setConfig] = useState(null);
  const [office, setOffice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tokenInput, setTokenInput] = useState("");
  const [cutoffInput, setCutoffInput] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [savingCfg, setSavingCfg] = useState(false);
  const [botBusy, setBotBusy] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loadingNotif, setLoadingNotif] = useState(false);
  const [officeForm, setOfficeForm] = useState(null);
  const [savingOffice, setSavingOffice] = useState(false);

  const fetchAll = async () => {
    try {
      const [u, c, o] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/config"),
        api.get("/admin/office"),
      ]);
      setUsers(u.data);
      setConfig(c.data);
      setOffice(o.data);
      setOfficeForm({
        name: o.data.name || "",
        location: o.data.location || "",
        contact_email: o.data.contact_email || "",
        is_public: o.data.is_public,
        default_cutoff_time: o.data.default_cutoff_time || "11:30",
      });
      setCutoffInput(c.data.default_cutoff_time || "11:30");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    setLoadingNotif(true);
    try {
      const { data } = await api.get("/admin/notifications?limit=50");
      setNotifications(data.items);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingNotif(false);
    }
  };

  useEffect(() => {
    fetchAll();
    fetchNotifications();
  }, []);

  const setRole = async (id, role) => {
    try {
      await api.put(`/admin/users/${id}/role`, { role });
      toast.success("Ruolo aggiornato");
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteUser = async (id) => {
    if (!window.confirm("Eliminare questo utente?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Utente eliminato");
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      const body = {};
      if (tokenInput.trim()) body.telegram_bot_token = tokenInput.trim();
      if (cutoffInput) body.default_cutoff_time = cutoffInput;
      if (!Object.keys(body).length) {
        toast.info("Niente da salvare");
      } else {
        await api.put("/admin/config", body);
        toast.success("Configurazione salvata");
        setTokenInput("");
        fetchAll();
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingCfg(false);
    }
  };

  const toggleBot = async () => {
    setBotBusy(true);
    try {
      if (config.bot_running) {
        await api.post("/telegram/stop");
        toast.success("Bot fermato");
      } else {
        await api.post("/telegram/start");
        toast.success("Bot avviato");
      }
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBotBusy(false);
    }
  };

  const removeToken = async () => {
    if (!window.confirm("Rimuovere il token e disattivare il bot?")) return;
    setBotBusy(true);
    try {
      await api.delete("/admin/config/telegram-token");
      toast.success("Token rimosso e bot disattivato");
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBotBusy(false);
    }
  };

  const saveOffice = async (e) => {
    e?.preventDefault?.();
    setSavingOffice(true);
    try {
      await api.put("/admin/office", officeForm);
      toast.success("Sede aggiornata");
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingOffice(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento…
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-6 sm:py-10">
      <div className="flex items-center gap-2 text-sm text-muted-foreground uppercase tracking-widest font-medium mb-2">
        <Settings className="w-4 h-4" /> Admin
      </div>
      <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mb-8">
        Pannello amministrazione
      </h1>

      {/* La mia sede */}
      {office && officeForm && (
        <section className="bg-card border border-border rounded-lg p-6 sm:p-7 mb-8" data-testid="my-office-section">
          <div className="flex items-center justify-between gap-4 mb-1 flex-wrap">
            <h2 className="font-display text-xl font-medium flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" /> La mia sede
            </h2>
            <div className="text-xs text-muted-foreground">
              /{office.slug} {office.is_protected && <span className="ml-1 text-primary">🔒 protetta</span>}
            </div>
          </div>
          <p className="text-sm text-muted-foreground mb-5">
            Modifica i dati pubblici della tua sede. Lo slug (URL) e il bot token sono gestiti
            qui sotto e dal superadmin.
          </p>
          <form onSubmit={saveOffice} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">Nome sede</label>
                <input
                  required
                  value={officeForm.name}
                  onChange={(e) => setOfficeForm({ ...officeForm, name: e.target.value })}
                  data-testid="office-self-name"
                  className="w-full px-3 py-2.5 bg-white border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Città / Sede fisica</label>
                <input
                  value={officeForm.location}
                  onChange={(e) => setOfficeForm({ ...officeForm, location: e.target.value })}
                  data-testid="office-self-location"
                  className="w-full px-3 py-2.5 bg-white border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Email di contatto</label>
                <input
                  type="email"
                  value={officeForm.contact_email}
                  onChange={(e) => setOfficeForm({ ...officeForm, contact_email: e.target.value })}
                  data-testid="office-self-email"
                  className="w-full px-3 py-2.5 bg-white border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Cutoff predefinito</label>
                <input
                  type="time"
                  value={officeForm.default_cutoff_time}
                  onChange={(e) => setOfficeForm({ ...officeForm, default_cutoff_time: e.target.value })}
                  data-testid="office-self-cutoff"
                  className="w-full px-3 py-2.5 bg-white border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary text-sm"
                />
              </div>
            </div>
            <label className="flex items-start gap-3 p-3 bg-muted/30 rounded-md cursor-pointer">
              <input
                type="checkbox"
                checked={officeForm.is_public}
                onChange={(e) => setOfficeForm({ ...officeForm, is_public: e.target.checked })}
                data-testid="office-self-public"
                className="mt-0.5 accent-primary w-4 h-4"
              />
              <div className="flex-1 text-sm">
                <div className="font-medium">Visibile pubblicamente</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Se attivo, nuovi colleghi possono trovare la tua sede dalla pagina di registrazione.
                  Disattiva per nascondere e accettare solo registrazioni con link diretto.
                </div>
              </div>
            </label>
            <div className="flex items-center justify-between gap-3 pt-2 border-t border-border">
              <div className="text-xs text-muted-foreground">
                👤 <b>{office.users_count}</b> utenti · 🍽 <b>{office.dishes_count}</b> piatti attivi
              </div>
              <button
                type="submit"
                disabled={savingOffice}
                data-testid="office-self-save"
                className="px-4 py-2 bg-primary text-white font-medium text-sm rounded-md hover:bg-[#436343] active:scale-[0.98] disabled:opacity-50 transition-all"
              >
                {savingOffice ? "Salvataggio…" : "Salva modifiche"}
              </button>
            </div>
          </form>
        </section>
      )}

      {/* Telegram config */}
      <section className="bg-card border border-border rounded-lg p-6 sm:p-7 mb-8">
        <div className="flex items-center justify-between gap-4 mb-1 flex-wrap">
          <h2 className="font-display text-xl font-medium flex items-center gap-2">
            <Send className="w-5 h-5 text-primary" /> Bot Telegram
          </h2>
          <div className="flex items-center gap-2 text-sm" data-testid="bot-status">
            {config.bot_running ? (
              <><CheckCircle2 className="w-4 h-4 text-primary" /> <span>Attivo</span></>
            ) : (
              <><XCircle className="w-4 h-4 text-muted-foreground" /> <span>Spento</span></>
            )}
          </div>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          Configura il token del bot e avvia il servizio per abilitare le prenotazioni via Telegram.
        </p>

        {/* Setup guide */}
        <details className="mb-5 bg-muted/50 rounded-md" data-testid="bot-guide">
          <summary className="cursor-pointer px-4 py-3 text-sm font-medium flex items-center gap-2">
            <Bot className="w-4 h-4" /> Come ottenere il token (BotFather)
          </summary>
          <div className="px-4 pb-4 text-sm text-muted-foreground space-y-2 leading-relaxed">
            <p>
              <strong>1.</strong> Apri Telegram e cerca <a href="https://t.me/BotFather" target="_blank" rel="noreferrer" className="text-primary inline-flex items-center gap-1 hover:underline">@BotFather <ExternalLink className="w-3 h-3" /></a>
            </p>
            <p><strong>2.</strong> Invia il comando <code className="px-1 py-0.5 bg-white border border-border rounded text-xs">/newbot</code></p>
            <p><strong>3.</strong> Scegli un <em>nome</em> (es. "Schiscetta Ufficio") e uno <em>username</em> che finisca per "bot" (es. <em>schiscetta_ufficio_bot</em>).</p>
            <p><strong>4.</strong> BotFather ti darà un token tipo <code className="px-1 py-0.5 bg-white border border-border rounded text-xs">123456789:ABC-DEF...</code></p>
            <p><strong>5.</strong> Incollalo qui sotto, salva, poi premi <strong>Avvia bot</strong>.</p>
            <p className="pt-2 text-xs italic">Suggerimento: imposta anche un'immagine profilo e una descrizione con <code className="px-1 py-0.5 bg-white border border-border rounded text-xs">/setdescription</code>.</p>
          </div>
        </details>

        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1.5">Token bot</label>
            <div className="relative">
              <input
                type={showToken ? "text" : "password"}
                placeholder={config.telegram_bot_token_set ? `Attuale: ${config.telegram_bot_token_masked}` : "Incolla il token di BotFather"}
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
                data-testid="bot-token-input"
                className="w-full px-3 py-2.5 pr-10 bg-white border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary text-sm"
              />
              <button
                type="button"
                onClick={() => setShowToken((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground p-1"
              >
                {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {config.telegram_bot_token_set ? "Token già configurato. Inserisci uno nuovo per sovrascriverlo." : "Nessun token impostato."}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">Cutoff predefinito</label>
            <input
              type="time"
              value={cutoffInput}
              onChange={(e) => setCutoffInput(e.target.value)}
              data-testid="default-cutoff-input"
              className="w-full px-3 py-2.5 bg-white border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary text-sm"
            />
            <p className="text-xs text-muted-foreground mt-1">Suggerito ai preparatori per i nuovi piatti.</p>
          </div>
        </div>

        <div className="flex gap-3 flex-wrap">
          <button
            onClick={saveConfig}
            disabled={savingCfg}
            data-testid="save-config-button"
            className="px-5 py-2.5 bg-foreground text-white font-medium rounded-md hover:bg-foreground/90 active:scale-[0.98] disabled:opacity-50 transition-all"
          >
            {savingCfg ? "Salvataggio…" : "Salva configurazione"}
          </button>
          <button
            onClick={toggleBot}
            disabled={botBusy || (!config.telegram_bot_token_set && !config.bot_running)}
            data-testid="toggle-bot-button"
            className={`px-5 py-2.5 font-medium rounded-md active:scale-[0.98] disabled:opacity-50 transition-all ${
              config.bot_running
                ? "bg-accent text-white hover:bg-[#C05A4A]"
                : "bg-primary text-white hover:bg-[#436343]"
            }`}
          >
            {botBusy ? "Attendere…" : config.bot_running ? "Ferma bot" : "Avvia bot"}
          </button>
          {config.telegram_bot_token_set && (
            <button
              onClick={removeToken}
              disabled={botBusy}
              data-testid="remove-token-button"
              className="px-5 py-2.5 border border-accent/30 text-accent font-medium rounded-md hover:bg-accent hover:text-white active:scale-[0.98] disabled:opacity-50 transition-all"
            >
              Rimuovi token
            </button>
          )}
        </div>
      </section>

      {/* Users */}
      <section className="bg-card border border-border rounded-lg overflow-hidden mb-8">
        <div className="p-6 sm:p-7 border-b border-border">
          <h2 className="font-display text-xl font-medium flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" /> Utenti
          </h2>
          <p className="text-sm text-muted-foreground mt-1">Gestisci ruoli e accessi.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/40">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3 font-medium">Nome</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Ruolo</th>
                <th className="px-4 py-3 font-medium">Telegram</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-border" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-medium">{u.name}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{u.email}</td>
                  <td className="px-4 py-3">
                    <select
                      value={u.role}
                      onChange={(e) => setRole(u.id, e.target.value)}
                      data-testid={`role-select-${u.id}`}
                      className="px-2 py-1.5 bg-white border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    >
                      <option value="ordinante">ordinante</option>
                      <option value="preparatore">preparatore</option>
                      <option value="admin">admin</option>
                    </select>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {u.telegram_chat_id ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-primary/10 text-primary text-xs">
                        <CheckCircle2 className="w-3 h-3" /> collegato
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => deleteUser(u.id)}
                      data-testid={`delete-user-${u.id}`}
                      className="text-xs text-accent hover:underline"
                    >
                      Elimina
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Notifications log */}
      <section className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="p-6 sm:p-7 border-b border-border flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display text-xl font-medium flex items-center gap-2">
              <Bell className="w-5 h-5 text-primary" /> Notifiche bot
            </h2>
            <p className="text-sm text-muted-foreground mt-1">Storico audit degli ultimi messaggi Telegram inviati.</p>
          </div>
          <button
            onClick={fetchNotifications}
            disabled={loadingNotif}
            data-testid="refresh-notifications"
            className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loadingNotif ? "animate-spin" : ""}`} /> Aggiorna
          </button>
        </div>
        {loadingNotif && notifications.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Caricamento…
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground" data-testid="notifications-empty">
            Nessuna notifica registrata ancora.
          </div>
        ) : (
          <div className="max-h-[480px] overflow-auto divide-y divide-border" data-testid="notifications-list">
            {notifications.map((n) => (
              <NotificationRow key={n.id} n={n} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

const KIND_LABEL = {
  new_order: { label: "Prenotazione", color: "bg-primary/10 text-primary" },
  summary: { label: "Riepilogo", color: "bg-blue-100 text-blue-700" },
  reminder: { label: "Reminder", color: "bg-amber-100 text-amber-700" },
  new_dish_broadcast: { label: "Nuovo piatto", color: "bg-purple-100 text-purple-700" },
};

const NOTIF_TIME = new Intl.DateTimeFormat("it-IT", {
  day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
});

function NotificationRow({ n }) {
  const meta = KIND_LABEL[n.kind] || { label: n.kind, color: "bg-muted text-muted-foreground" };
  return (
    <div className="px-4 py-3 flex items-start gap-3" data-testid={`notification-${n.id}`}>
      <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold flex-shrink-0 ${meta.color}`}>
        {meta.label}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap mb-1">
          <span>{n.created_at ? NOTIF_TIME.format(new Date(n.created_at)) : "—"}</span>
          {n.user_name && <><span>·</span><span>→ {n.user_name}</span></>}
          {n.dish_name && <><span>·</span><span>{n.dish_name}</span></>}
          {!n.success && <span className="text-accent">· fallita</span>}
        </div>
        <pre className="text-xs whitespace-pre-wrap font-body line-clamp-3 text-foreground/90">{n.text}</pre>
      </div>
    </div>
  );
}
